import type { MaterialUsageLine, MaterialUsagePreview } from "@/lib/service/materialUsageClient";
import { ghostBtnClass, inputClass } from "./serviceShared";

const STATUS_LABEL: Record<MaterialUsageLine["match_status"], string> = {
  matched: "Matched",
  overridden: "Override",
  ambiguous: "Pick one",
  unmatched: "No catalog match",
  rematched: "Rematched",
};

export function ServiceMaterialUsageReview({
  preview,
  serialText,
  onSerialText,
  onPlant,
  onLine,
  onRemoveLine,
  readOnly,
  quantityOnly = false,
}: {
  preview: MaterialUsagePreview;
  serialText: string;
  onSerialText: (value: string) => void;
  onPlant: (plantId: string) => void;
  onLine: (index: number, patch: Partial<MaterialUsageLine>) => void;
  onRemoveLine: (index: number) => void;
  readOnly: boolean;
  quantityOnly?: boolean;
}) {
  const locked = readOnly || quantityOnly;
  const serialNote =
    serialText.trim() !== preview.serial_text.trim()
      ? "The serial is checked again when you save."
      : preview.serial_found
        ? "Found in the system."
        : "Not in the system — recorded as spoken.";

  return (
    <div className="space-y-3">
      <label className="block text-sm text-gray-700 dark:text-zinc-300">
        Serial
        <input className={`${inputClass} mt-1`} value={serialText} disabled={locked} onChange={(event) => onSerialText(event.target.value)} />
      </label>
      <p className="text-xs text-gray-500 dark:text-zinc-400">{serialNote}</p>
      <label className="block text-sm text-gray-700 dark:text-zinc-300">
        Plant
        <select
          className={`${inputClass} mt-1`}
          value={preview.plant_id ?? ""}
          disabled={locked}
          onChange={(event) => onPlant(event.target.value)}
        >
          <option value="">Select a plant</option>
          {preview.plants.map((plant) => (
            <option key={plant.id} value={plant.id}>
              {plant.name}
            </option>
          ))}
        </select>
      </label>
      {preview.plant_status === "ambiguous" ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">More than one plant matched. Pick the plant.</p>
      ) : null}
      <ul className="space-y-3">
        {preview.lines.map((line, index) => (
          <li key={`${line.spoken_phrase}-${index}`} className="rounded-xl border border-gray-200 p-3 dark:border-zinc-700">
            <div className="mb-2 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-zinc-400">
              <span>{STATUS_LABEL[line.match_status]}{line.unit ? ` · ${line.unit}` : ""}</span>
              {locked ? null : (
                <button type="button" className={ghostBtnClass} onClick={() => onRemoveLine(index)}>
                  Remove
                </button>
              )}
            </div>
            <div className="grid gap-2 sm:grid-cols-[6rem_1fr]">
              <input
                className={inputClass}
                type="number"
                step="any"
                value={line.quantity}
                disabled={readOnly && !quantityOnly}
                onChange={(event) => onLine(index, { quantity: Number(event.target.value) })}
              />
              <input
                className={inputClass}
                value={line.spoken_phrase}
                disabled={locked}
                onChange={(event) => onLine(index, { spoken_phrase: event.target.value })}
              />
            </div>
            <label className="mt-2 block text-sm text-gray-700 dark:text-zinc-300">
              Catalog material
              <select
                className={`${inputClass} mt-1`}
                disabled={locked || !preview.plant_id}
                value={line.material_id ?? ""}
                onChange={(event) => {
                  const materialId = event.target.value;
                  const material = preview.catalog.find((row) => row.id === materialId);
                  onLine(index, {
                    material_id: materialId || null,
                    material_name: material?.name ?? null,
                    unit: material?.unit ?? null,
                    match_status: materialId ? "overridden" : "unmatched",
                  });
                }}
              >
                <option value="">No match</option>
                {line.candidates.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </option>
                ))}
                {preview.catalog
                  .filter((material) => !line.candidates.some((candidate) => candidate.id === material.id))
                  .map((material) => (
                    <option key={material.id} value={material.id}>
                      {material.name}
                    </option>
                  ))}
              </select>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
